// PiecePreview - ピースのテストプレイ画面（spec.md §5.7）
// ビルダーの現在マップを 3D 表示し、テスト実行で完成判定の tested を満たす。
// Blockly 実行エンジンは algorithm コース流用予定（TODO）。API はスタブ。

import { useState } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Scene } from "@babylonjs/core/scene";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { RootState } from "app/store";
import I18n from "common/i18n";
import { useScale, usePlaySound, btnDelay } from "common/utils";
import { actions } from "features/builder/slice";
import { playPiece } from "features/builder/api";
import { BuilderScene } from "features/builder/canvas/scene";
import { BuilderProvider } from "features/builder/context/BuilderContext";

export const PiecePreview = () => {
  const play = usePlaySound();
  const { scale } = useScale();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const name = useSelector((state: RootState) => state.builder.name);
  const pieceId = useSelector((state: RootState) => state.builder.editingBuilderMapId);
  const enabledBlocks = useSelector((state: RootState) => state.builder.enabledBlocks);
  const active = useSelector((state: RootState) => state.user.active);

  const [scene, setScene] = useState<Scene | null>(null);
  const [moves, setMoves] = useState(0);

  const handleBack = () => {
    play();
    btnDelay(() => navigate(-1));
  };

  const handleReset = () => {
    play();
    btnDelay(() => setMoves(0));
  };

  // TODO(piece-builder): algorithm コースの Blockly 実行エンジンを流用して
  // 実際のキャラ移動・クリア判定を実装する。現状はテスト完了申告のみ。
  const handleRun = () => {
    play();
    btnDelay(async () => {
      setMoves((prev) => prev + 1);
      dispatch(actions.markTested());
      await playPiece({ piece_id: pieceId, uid: active.uid, cleared: true });
      toast(I18n.t("MSG_BUILDER_PREVIEW_CLEAR"));
    });
  };

  return (
    <BuilderProvider>
      <div className="flex-col-el w-full h-full relative bg-textcolor-black">
        {/* 3D シーン（現在のビルダーマップ） */}
        <div className="absolute inset-0">
          <BuilderScene scene={scene} setScene={setScene} />
        </div>

        {/* タイトル */}
        <div
          className="flex-col-el absolute origin-top-left"
          style={{ top: 20 * scale, left: 20 * scale, transform: `scale(${scale})` }}
        >
          <div className="flex-row-el items-center gap-2 rounded-2xl bg-white/90 px-4 py-2 shadow-lg">
            <span className="type-title">🧩</span>
            <span className="type-title text-textcolor-black">{name}</span>
          </div>
        </div>

        {/* 戻る */}
        <div
          className="flex-col-el absolute origin-top-right"
          style={{ top: 20 * scale, right: 20 * scale, transform: `scale(${scale})` }}
        >
          <div className="rho">
            <button
              onClick={handleBack}
              className="btn type-inherit-font type-sm"
              style={{ margin: 0 }}
            >
              <div className="flex-row-el flex-center gap-1">
                <span className="flex-col-el flex-center type-md">
                  <ArrowBackIcon fontSize="inherit" />
                </span>
                {I18n.t("MSG_BUILDER_PREVIEW_BACK")}
              </div>
            </button>
          </div>
        </div>

        {/* 下部コントロールバー */}
        <div
          className="flex-col-el flex-center absolute inset-x-0 bottom-0 origin-bottom"
          style={{ transform: `scale(${scale})`, marginBottom: 24 * scale }}
        >
          <div className="flex-row-el items-center gap-4 rounded-2xl bg-white/90 px-6 py-3 shadow-lg">
            <div className="flex-col-el items-center">
              <span className="type-sm text-gray-500">{I18n.t("MSG_BUILDER_PREVIEW_MOVES")}</span>
              <span className="type-body text-textcolor-black">{moves}</span>
            </div>

            <div className="upsilon">
              <button
                onClick={handleReset}
                className="btn btn-blue type-inherit-font type-sm"
                style={{ margin: 0 }}
              >
                <div className="flex-row-el flex-center gap-1">
                  <span className="flex-col-el flex-center type-md">
                    <RestartAltIcon fontSize="inherit" />
                  </span>
                  {I18n.t("MSG_BUILDER_PREVIEW_RESET")}
                </div>
              </button>
            </div>

            <div className="alpha">
              <button
                onClick={handleRun}
                className="btn btn-primary type-inherit-font type-body"
                style={{ margin: 0 }}
              >
                <div className="flex-row-el flex-center gap-1">
                  <span className="flex-col-el flex-center type-title">
                    <PlayArrowIcon fontSize="inherit" />
                  </span>
                  {I18n.t("MSG_BUILDER_PREVIEW_RUN")}
                </div>
              </button>
            </div>
          </div>
        </div>

        {/* TODO(piece-builder): enabled_blocks=[{enabledBlocks.join(',')}] で
            Blockly ツールボックスを制限したワークスペースを algorithm コースから流用 */}
        <div className="hidden">{enabledBlocks.join(",")}</div>
      </div>
    </BuilderProvider>
  );
};
